/**
 * The shared chrome for every signed-in manager page.
 *
 * ## Why this exists
 *
 * Lloyd, Aug 19 2026, on the letter editor: *"there was no back link to take me out of it, or the
 * menu in the upper right corner."* The immediate cause was that `ManagerLettersView` never
 * rendered `ManagerNav` — it was the only manager page in the app that did not, so once you were on
 * `/letters` the browser back button was genuinely the only way out.
 *
 * The underlying cause was that **every view imported and rendered the nav itself**, which made a
 * new page exactly one forgotten import away from being unreachable. Fixing the one page did not
 * fix that, and the next new page would have found the same trap.
 *
 * Now the router mounts this once and the nav is structural. A page cannot forget it, because a
 * page no longer renders it.
 *
 * ## What is deliberately NOT wrapped
 *
 * Three routes stay outside, and each for its own reason:
 *
 *  - **`/manager-reset-password`** — reached without a session. A nav full of links to pages the
 *    visitor cannot open is worse than no nav.
 *  - **`/case/:token`** — the partner case link. Referral partners have no login at all; showing
 *    them the firm's internal menu would be a disclosure decision made by a layout.
 *
 * `*` (not found) IS wrapped, on purpose: a signed-in manager who mistypes a URL is precisely the
 * person who needs a way out, and that is the same complaint Lloyd made.
 */
import React from 'react';
import { Outlet } from 'react-router-dom';
import ManagerNav from '../ManagerNav/ManagerNav';
import { CreateCaseProvider } from '../CreateCaseFromDocument/CreateCaseContext';

const ManagerLayout: React.FC = () => (
  // "Create case" from any staff page — the top-bar button, or a document dropped or pasted anywhere
  // (Lloyd, Oct 2026). Here rather than in App so the client portal and public pages never get it.
  <CreateCaseProvider>
    {/*
      `AppBar position="static"`, so this sits in the document flow exactly where each view used to
      render it. Nothing below needs to compensate for it.
    */}
    <ManagerNav />
    <Outlet />
  </CreateCaseProvider>
);

export default ManagerLayout;
